import React, { useEffect, useState, useRef, memo } from "react";
import { TableCell, TableRow, Typography, Select, MenuItem, TextField, IconButton, Chip, Box, Skeleton, Tooltip } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import QuantityControl from "../../../common/QuantityControl";
import productSplitService from "../../../../services/ProductSplitService";
import { calculateItemVat, getPriceForItem, calculateItemSubtotal, parseQty, resolveCartLineDisplayTotal, resolveCartLineInlinePromoGrossDisplayTotal, resolveCartLineStrikethroughPrice, resolveOfferOrSalesUnitPrice, resolveProductTaxCode, resolveCartLineInlinePromoDiscountForDisplay, shouldShowCartLineInlinePromoDiscountFromBuckets, shouldShowCartPromotionSummarySubRowFromBuckets, resolveCartPromotionSummaryBuyDiscountForDisplayFromBuckets, resolveCartPromotionSummarySubRowCountFromBuckets, resolveRhpCartLineListPrice, resolveRhpCartLinePriceDiscount, isRhpRefundCartLine, getCartItemNormalizedFields, getCartLineDiscountComment } from "../utils/cartCalculations";
import { shouldSkipTelecallerStockLimit } from "../utils/telecallerStockLimit";
import { getQtyControlMaxFromBuckets } from "../utils/cartQtyValidation";
import { PREFIX, retainsCartDiscountFocus } from "../../../common/Config";
import { resolveIsPriceEditable } from "../../../../utils/companySettingsCache";
import { useCompanySettings } from "../../../../contexts/CompanySettingsContext";
import { getCartTableKeyboardHighlightSx, getRhpCartTableRowSelectionSx } from "../utils/cartTableKeyboard";

const DISABLE_DISC_COLUMN_WHEN_PROMOTED = true;
const DEFAULT_DISCOUNT_TYPE = PREFIX === "tsc" ? "percentage" : "amount";

function CartClickToEditValue({
  value,
  onStartEdit,
  disabled = false,
  width,
  align = "center",
  error = false,
  bordered = false,
  dataAttrs = null,
}) {
  const style = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: align === "right" ? "flex-end" : "center",
    minHeight: 28,
    width: width || undefined,
    padding: "0 6px",
    margin: 0,
    border: error ? "1px solid #d32f2f" : bordered ? "1px solid #000" : "1px solid transparent",
    borderRadius: 4,
    background: bordered ? "#fff" : "transparent",
    cursor: disabled ? "default" : "pointer",
    fontSize: "0.75rem",
    fontWeight: 600,
    fontFamily: "inherit",
    color: "inherit",
    opacity: disabled ? 0.6 : 1,
    boxSizing: "border-box",
  };
  if (disabled) {
    return <span style={style}>{value}</span>;
  }
  return (
    <button type="button" style={style} onClick={onStartEdit} {...(dataAttrs || {})}>
      {value}
    </button>
  );
}

function CartDiscountHtmlField({
  value,
  discountType,
  disabled = false,
  error = false,
  onChange,
  onBlur,
  onFocus,
  onTypeChange,
  dataCartDiscountInput = false,
  height = 28,
}) {
  const typeNorm = String(discountType || "").trim().toLowerCase();
  const selectValue = typeNorm === "percentage" || typeNorm === "%" ? "percentage" : "amount";
  const selectLabel = selectValue === "percentage" ? "%" : "£";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        width: 96,
        minWidth: 96,
        height,
        border: error ? "1px solid #d32f2f" : "1px solid #000",
        borderRadius: 4,
        background: "#fff",
        boxSizing: "border-box",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        type="text"
        inputMode="decimal"
        placeholder="0"
        disabled={disabled}
        value={disabled ? "" : (value ?? "")}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onWheel={(e) => e.target.blur()}
        {...(dataCartDiscountInput ? { "data-cart-discount-input": true } : {})}
        style={{
          width: 54,
          flex: 1,
          minWidth: 0,
          height: "100%",
          border: "none",
          outline: "none",
          textAlign: "right",
          fontSize: "0.75rem",
          fontWeight: 600,
          fontFamily: "inherit",
          padding: "0 4px",
          background: "#fff",
          color: "#000",
          boxSizing: "border-box",
        }}
      />
      <span
        style={{
          position: "relative",
          width: 40,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderLeft: error ? "1px solid #d32f2f" : "1px solid #000",
          background: "#fff",
          color: "#000",
          fontSize: "0.8rem",
          fontWeight: 700,
          fontFamily: "inherit",
          lineHeight: 1,
        }}
      >
        <span style={{ pointerEvents: "none" }}>{selectLabel}</span>
        <span style={{ pointerEvents: "none", fontSize: "0.65rem", marginLeft: 2 }}>▾</span>
        <select
          value={selectValue}
          disabled={disabled}
          onChange={onTypeChange}
          aria-label="Discount type"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: disabled ? "default" : "pointer",
            fontSize: "0.75rem",
          }}
        >
          <option value="amount">£</option>
          <option value="percentage">%</option>
        </select>
      </span>
    </span>
  );
}

const CartTableRow = ({
  item,
  index,
  onTypeChange,
  onAddAsNewType = null,
  onQuantityChange,
  onPriceChange,
  onDiscountChange,
  onDiscountCommentChange = null,
  onDelete,
  showVatAsAmount = false,
  showVatBoth = false,
  enableVatTooltip = true,
  isLoading = false,
  itemDetails = null,
  itemNorm = null,
  onValidationError = null,
  customerId = null,
  isEditMode = false,
  isQuickOrder = false,
  onAllotQtyChange = null,
  autoOpenQuantityPopup = false,
  autoFocusQuantityInput = false,
  quantityInlineEditKey = "",
  onQuantityPopupClose = null,
  onQuantityPopupOpen = null,
  onDiscountChangeComplete = null,
  onPriceChangeComplete = null,
  enableProductSplit = false,
  isRhp = false,
  onPromoQtyLineOpenDrawer = null,
  onPromoQtyOpenPromotionsModal = null,
  isKeyboardHighlighted = false,
  onQtyRegionFocusChange = null,
  qtyControlBucketItems = null,
  promoGroupItems = null,
  bundleDiscounts = [],
  bundleLineDiscountByKey = null,
  isTelecaller = false,
  hideTypeColumn = false,
  hideBoxPriceColumn = false,
  hideVatColumn = false,
  rhpReadOnlyPricing = false,
}) => {
  const n = itemNorm || getCartItemNormalizedFields(item);
  const allowDecimalOnRow = n.isMeats || (PREFIX === "atr" && isEditMode);
  const code = item.code || item.product_code || "";
  const description = item.desc || item.name || item.product_name || "";
  const promotion_name = item.promotion_name || item.promotionName || item.promo_name || "";
  const discountCommentTrimmed = getCartLineDiscountComment(item);
  const orderType = item.type || item.order_type || n.orderType;
  const unitOrderTypeLabel = String(item.unit_order_type ?? "").trim();
  const displayOrderType = PREFIX === "atr" && isEditMode && unitOrderTypeLabel ? unitOrderTypeLabel : orderType;
  const qty =
    isEditMode &&
    item.order_qty !== undefined &&
    item.order_qty !== null &&
    item.order_qty !== ""
      ? n.orderQty
      : n.qty;
  const currentPrice = n.price;
  const discount = (() => {
    if (n.overrideDiscount !== null && n.overrideDiscount >= 0) {
      return n.overrideDiscount;
    }
    if (!isEditMode && n.productWiseDiscountValue !== null && n.productWiseDiscountValue >= 0) {
      return n.productWiseDiscountValue;
    }
    if (n.itemDiscount !== null && n.itemDiscount >= 0) {
      return n.itemDiscount;
    }
    if (n.discountRaw !== null && n.discountRaw >= 0) {
      return n.discountRaw;
    }
    return 0;
  })();
  const vatRate = n.directVatRate;
  const isContractPrice = n.isContractPrice;

  const canSplit = (() => {
    if (item.split === "2") return true;
    if (enableProductSplit && customerId && item.product_id) {
      return productSplitService.canSplit(customerId, item.product_id);
    }
    return false;
  })();

  const originalPrice = n.displayOriginalPrice;

  const itemDiscountType = (() => {
    if (n.overrideDiscountType) {
      return n.overrideDiscountType;
    }
    if (!isEditMode && n.productWiseDiscountType) {
      return n.productWiseDiscountType;
    }
    return n.discountTypeRaw || n.productWiseDiscountType || DEFAULT_DISCOUNT_TYPE;
  })();
  const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
  const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

  const is100PercentDiscount = !isFixedDiscount && (discount === 100 || discount === 100.0);
  const isRefundLine = rhpReadOnlyPricing && isRhpRefundCartLine(item);
  const shouldShowRedText = (PREFIX === "tsc" && is100PercentDiscount) || isRefundLine;

  const actualPrice = n.actualPrice;
  const discountPercentage = n.discountPercentage;
  const hasPriceDifference = originalPrice > 0 && Math.abs(originalPrice - actualPrice) > 0.01;
  const shouldShowOriginalPrice = (discount > 0 || discountPercentage > 0 || hasPriceDifference) && originalPrice > 0 && hasPriceDifference;
  const hasActualDiscount = discount > 0 || discountPercentage > 0
    || parseFloat(item.total_discount || 0) > 0
    || parseFloat(item.promotion_discount || 0) > 0
    || parseFloat(item.line_discount || 0) > 0
    || parseFloat(item.discount_amount || 0) > 0;

  const hasManualDiscount = n.overrideDiscount !== null && n.overrideDiscount > 0;
  const isFree = itemDetails ? itemDetails.isFree : n.isFree;
  const isPromoted = itemDetails ? itemDetails.isPromoted : n.isPromoted;
  const isGetPromotion = itemDetails ? itemDetails.isGetPromotion : n.isGetPromotion;

  const checkEditModeDiscounted = () => {
    if (!isEditMode) return false;

    const promotionApplied = n.promotionApplied;
    const promotionId = item.promotion_id !== undefined && item.promotion_id !== null && item.promotion_id !== "";

    if (n.promotionExplicitlyRemoved) {
      return false;
    }

    const promoUnitPrice = Number.isFinite(n.promoUnitPrice) ? n.promoUnitPrice : null;
    const unitPrice = Number.isFinite(n.unitPrice) ? n.unitPrice : null;
    const baseUnitPrice = Number.isFinite(n.baseUnitPrice) ? n.baseUnitPrice : null;

    const hasPromotionApplied = promotionApplied || promotionId;

    const promoDiscPct = n.promoDiscPercentage;
    const promoDiscAmt = n.promoDiscAmount;
    if (hasPromotionApplied && promoDiscPct === 0 && promoDiscAmt === 0) {
      return false;
    }

    const hasLineDiscount = item.line_discount !== undefined && item.line_discount !== null && parseFloat(item.line_discount || 0) > 0;
    const hasDiscountPercentage = n.discountPercentage > 0;

    if (hasPromotionApplied && promoUnitPrice !== null && promoUnitPrice > 0) {
      const referencePrice = unitPrice !== null ? unitPrice : (baseUnitPrice !== null ? baseUnitPrice : null);
      if (referencePrice !== null && referencePrice > 0 && Math.abs(referencePrice - promoUnitPrice) > 0.01) {
        return true;
      }
    }

    if (hasPromotionApplied && (hasLineDiscount || hasDiscountPercentage)) {
      return true;
    }

    return false;
  };

  const normalizedPromoType = n.promoType;
  const isPromoTypeBuy = n.isPromoTypeBuy;
  const isGetPromoType = n.isGetPromoType;
  const isPromoTypeFree = n.isPromoTypeFree;
  const isPromotedFlag = n.isPromoted;
  const isGetPromotionResolved =
    isGetPromotion || (isPromotedFlag && isGetPromoType);
  const isOfferLineDisplay = n.isOfferDisplay;
  const isQuantityDisabled = isOfferLineDisplay;
  const showOriginalPrice = isEditMode
    ? checkEditModeDiscounted()
    : (isPromoted && (itemDetails?.isDiscounted || false));
  const isDisabled = isQuantityDisabled;
  const qtyControlMax = !shouldSkipTelecallerStockLimit(item)
    ? getQtyControlMaxFromBuckets(item, "qty", qtyControlBucketItems)
    : allowDecimalOnRow
      ? 9999.99
      : 9999;
  const isFreeItem = isOfferLineDisplay && !isGetPromoType;
  const isItemPromoted = n.isItemPromoted;
  const isPromotionApplied = item.is_promoted === 0;
  const promotionApplied = n.promotionApplied;
  const hasValidPromotionId = n.hasValidPromotionId;
  const promotionExplicitlyRemoved = n.promotionExplicitlyRemoved;
  const hasPromoDiscountAmount = n.hasPromoDiscountAmount;
  const hasManualDiscountForPromoIndicator =
    (n.overrideDiscount !== null && n.overrideDiscount > 0) ||
    (!promotionApplied && discount > 0 && !hasPromoDiscountAmount);
  const hasStalePromoMetadataShape =
    isItemPromoted ||
    hasValidPromotionId ||
    normalizedPromoType === "BUY" ||
    normalizedPromoType === "GET";
  const suppressPromoIndicator =
    promotionExplicitlyRemoved ||
    (!promotionApplied &&
      !hasPromoDiscountAmount &&
      hasManualDiscountForPromoIndicator &&
      hasStalePromoMetadataShape);
  const showPromoIndicator =
    !suppressPromoIndicator &&
    (promotionApplied || hasValidPromotionId || isItemPromoted);
  const promotionNameTrimmed = String(promotion_name || "").trim();
  const showPromotionNameLabel =
    showPromoIndicator && !!promotionNameTrimmed && item.showPromo === true;
  const hasPromoDiscPct = n.promoDiscPercentage > 0;
  const promoIndicatorLetter =
    normalizedPromoType === "FREE" && showPromoIndicator
      ? "F"
      : (normalizedPromoType === "GET" || normalizedPromoType === "BUY") && showPromoIndicator
        ? "P"
        : isItemPromoted && showPromoIndicator && isPromotionApplied
          ? "P"
          : null;
  const promoAlternateQtyButtons = isOfferLineDisplay;
  const isPromoFreeOrGetProductName = isOfferLineDisplay;
  const showPromotionSummarySubRow = shouldShowCartPromotionSummarySubRowFromBuckets(
    item,
    bundleDiscounts,
    promoGroupItems
  );
  const promotionSummaryBuyDiscountNet = resolveCartPromotionSummaryBuyDiscountForDisplayFromBuckets(
    item,
    bundleDiscounts,
    promoGroupItems
  );
  const promotionSummarySubRowCount = showPromotionSummarySubRow
    ? resolveCartPromotionSummarySubRowCountFromBuckets(item, promoGroupItems)
    : 0;
  const promotionSummaryUnitDiscount =
    promotionSummarySubRowCount > 1
      ? promotionSummaryBuyDiscountNet / promotionSummarySubRowCount
      : promotionSummaryBuyDiscountNet;
  const inlinePromoDiscountNet = resolveCartLineInlinePromoDiscountForDisplay(item);
  const showInlinePromoDiscount = shouldShowCartLineInlinePromoDiscountFromBuckets(item, promoGroupItems);
  const showPromotionNameInlineUnderDescription =
    showPromotionNameLabel && !showPromotionSummarySubRow;
  const keyboardHighlightSx = isKeyboardHighlighted
    ? (rhpReadOnlyPricing
        ? getRhpCartTableRowSelectionSx()
        : getCartTableKeyboardHighlightSx())
    : {};
  const isPromotionLockedNoManualDiscount = promotionApplied && (isPromoTypeFree || isGetPromoType || isPromoTypeBuy) && hasPromoDiscPct;
  const isDiscountDisabled =
    isFree ||
    isGetPromotionResolved ||
    isGetPromoType ||
    isItemPromoted ||
    isPromotionLockedNoManualDiscount;
  const hasPromoId = !!(item.promosId || item.promoId || item.promotion_id);
  const isPriceDisabled = isDisabled || hasPromoId;

  const getPriceDisplayValue = () => {
    if ((PREFIX === "tsc" || PREFIX === "dan") && isEditMode && (item.useBackendCalculations || (item.unit_price !== undefined && item.unit_price !== null))) {
      const unitPriceVal = parseFloat(item.unit_price);
      if (Number.isFinite(unitPriceVal)) return unitPriceVal.toFixed(2);
    }
    let priceValue;
    if (item.manualOverrides?.price !== undefined) {
      priceValue = parseFloat(item.manualOverrides.price);
    } else if (typeof item.price === "number") {
      priceValue = item.price;
    } else if (typeof item.price === "string" && item.price.length > 0) {
      priceValue = parseFloat(item.price);
    } else {
      priceValue = currentPrice;
    }

    if (Number.isFinite(priceValue)) {
      return String(priceValue);
    }
    return "";
  };

  const [priceInput, setPriceInput] = useState(getPriceDisplayValue);
  const [priceError, setPriceError] = useState(false);
  const [discountError, setDiscountError] = useState(false);
  const getInitialDiscountType = () => {
    if (isEditMode && (discount === 0 || discount === "" || discount === null || discount === undefined)) {
      return DEFAULT_DISCOUNT_TYPE;
    }
    if (item.manualOverrides?.discount_type) {
      return item.manualOverrides.discount_type;
    }
    if (!isEditMode && item.productWiseDiscountType) {
      return item.productWiseDiscountType;
    }
    return item.discount_type || item.productWiseDiscountType || DEFAULT_DISCOUNT_TYPE;
  };
  const initialDiscountType = getInitialDiscountType();
  const [discountType, setDiscountType] = useState(initialDiscountType);

  const getInitialDiscountInput = () => {
    if (item.manualOverrides?.discount !== undefined && item.manualOverrides?.discount !== null && item.manualOverrides?.discount !== "") {
      const value = parseFloat(item.manualOverrides.discount);
      return value === 0 ? "" : String(value);
    }
    if (!isEditMode && item.productWiseDiscountValue !== undefined && item.productWiseDiscountValue !== null && item.productWiseDiscountValue !== "") {
      const value = parseFloat(item.productWiseDiscountValue);
      return value === 0 ? "" : String(value);
    }
    return discount === 0 ? "" : String(discount);
  };

  const [discountInput, setDiscountInput] = useState(getInitialDiscountInput());
  const [editingCell, setEditingCell] = useState(null);
  const prevDiscountRef = useRef(discount);
  const prevDiscountTypeRef = useRef(initialDiscountType);
  const userChangedDiscountTypeRef = useRef(false);
  const discountDebounceTimerRef = useRef(null);
  const discountInputDraftRef = useRef("");
  const isDiscountInputFocusedRef = useRef(false);
  const isPriceInputFocusedRef = useRef(false);
  const isAllotQtyInputFocusedRef = useRef(false);
  const priceDebounceTimerRef = useRef(null);
  const tableRowRef = useRef(null);

  const allotQty = item.allot_qty !== undefined && item.allot_qty !== null && item.allot_qty !== ""
    ? item.allot_qty
    : (item.accept_qty !== undefined && item.accept_qty !== null && item.accept_qty !== ""
      ? item.accept_qty
      : String(qty));
  const [allotQtyInput, setAllotQtyInput] = useState(String(allotQty));
  const [allotQtyError, setAllotQtyError] = useState(false);

  const { settings: companySettings, loaded: companySettingsLoaded } = useCompanySettings();
  const isPriceEditableFromSettings = resolveIsPriceEditable(companySettings);
  const isPriceEditableFromSettingsLoaded = companySettingsLoaded;

  const getUnitPriceDisplayValue = () => {
    const val = parseFloat(item.unit_price ?? item.price ?? 0);
    return Number.isFinite(val) ? val.toFixed(2) : "";
  };
  const [unitPriceInput, setUnitPriceInput] = useState(getUnitPriceDisplayValue);
  const [unitPriceError, setUnitPriceError] = useState(false);
  const unitPriceInputFocusedRef = useRef(false);
  const justCommittedUnitPriceRef = useRef(false);
  const allowEveUnitPriceEdit =
    !!onPriceChange &&
    !rhpReadOnlyPricing &&
    !(isQuickOrder && isRhp) &&
    !isItemPromoted &&
    (isPriceEditableFromSettingsLoaded ? isPriceEditableFromSettings : true);
  const rhpListPrice = rhpReadOnlyPricing ? resolveRhpCartLineListPrice(item) : 0;
  const rhpPriceDiscount = rhpReadOnlyPricing ? resolveRhpCartLinePriceDiscount(item) : 0;

  useEffect(() => {
    if (!allowEveUnitPriceEdit || unitPriceInputFocusedRef.current) return;
    if (justCommittedUnitPriceRef.current) {
      justCommittedUnitPriceRef.current = false;
      return;
    }
    setUnitPriceInput(getUnitPriceDisplayValue());
    setUnitPriceError(false);
  }, [item.unit_price, item.price, allowEveUnitPriceEdit]);

  const updatedItem = {
    ...item,
    qty: qty,
    unit_price: item.unit_price || item.price,
    discount: discount,
    discount_type: discountType,
    tax_rate: vatRate
  };

  const vatAmount = (item.useBackendCalculations || isEditMode) && (item.line_vat !== undefined || item.line_tax !== undefined || item.vat !== undefined || item.tax !== undefined)
    ? parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0)
    : calculateItemVat(updatedItem);

  useEffect(() => {
    if (isEditMode && isAllotQtyInputFocusedRef.current) return;
    const newAllotQty = item.allot_qty !== undefined && item.allot_qty !== null && item.allot_qty !== ""
      ? item.allot_qty
      : (item.accept_qty !== undefined && item.accept_qty !== null && item.accept_qty !== ""
        ? item.accept_qty
        : String(qty));
    const allotDisplay = allowDecimalOnRow && Number.isFinite(Number(newAllotQty))
      ? Number(newAllotQty).toFixed(2)
      : String(newAllotQty);
    setAllotQtyInput(allotDisplay);
    setAllotQtyError(false);
  }, [item.allot_qty, item.accept_qty, qty, isEditMode]);

  useEffect(() => {
    if (isEditMode && isPriceInputFocusedRef.current) return;
    setPriceInput(getPriceDisplayValue());
    setPriceError(false);
  }, [item.manualOverrides?.price, item.price, item.unit_price, currentPrice, isEditMode]);

  useEffect(() => {
    if (userChangedDiscountTypeRef.current) {
      let itemDiscountType;
      if (item.manualOverrides?.discount_type) {
        itemDiscountType = item.manualOverrides.discount_type;
      } else if (!isEditMode && item.productWiseDiscountType) {
        itemDiscountType = item.productWiseDiscountType;
      } else {
        itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
      }
      if (itemDiscountType === discountType) {
        userChangedDiscountTypeRef.current = false;
      }
      return;
    }

    let itemDiscountType;
    if (item.manualOverrides?.discount_type) {
      itemDiscountType = item.manualOverrides.discount_type;
    } else if (!isEditMode && item.productWiseDiscountType) {
      itemDiscountType = item.productWiseDiscountType;
    } else {
      itemDiscountType = item.discount_type || item.productWiseDiscountType || DEFAULT_DISCOUNT_TYPE;
    }

    if (isEditMode) {
      const currentDiscount = (() => {
        const manualDiscount = item.manualOverrides?.discount;
        if (manualDiscount !== undefined && manualDiscount !== null && manualDiscount !== "") {
          return parseFloat(manualDiscount);
        }
        return parseFloat(item.discount || 0);
      })();

      if (currentDiscount === 0 || !Number.isFinite(currentDiscount)) {
        itemDiscountType = DEFAULT_DISCOUNT_TYPE;
      }
    }

    if (itemDiscountType !== discountType) {
      setDiscountType(itemDiscountType);
      prevDiscountTypeRef.current = itemDiscountType;
    }
  }, [item.discount_type, item.manualOverrides?.discount_type, item.productWiseDiscountType, isEditMode, item.discount, item.manualOverrides?.discount]);

  const isIncompleteDiscountDraft = (value) => {
    const draft = String(value ?? "").trim();
    return draft === "" || draft === "." || draft.endsWith(".");
  };

  const resolveDiscountUnitPrice = () => {
    let unitPrice = NaN;
    if (item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== "") {
      unitPrice = parseFloat(item.unit_price);
    } else if (item.manualOverrides?.price !== undefined && item.manualOverrides?.price !== null && item.manualOverrides?.price !== "") {
      unitPrice = parseFloat(item.manualOverrides.price);
    } else if (orderType === "Box") {
      unitPrice = parseFloat(item.offer_box_price || item.sales_box_price || item.price || 0);
    } else {
      unitPrice = parseFloat(item.offer_piece_price || item.sales_piece_price || item.price || 0);
    }
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
      unitPrice = parseFloat(item.price || currentPrice || 0);
    }
    return Number.isFinite(unitPrice) ? unitPrice : 0;
  };

  const isDiscountValueInvalid = (numValue, type = discountType) => {
    if (!Number.isFinite(numValue) || numValue < 0) return true;
    const normalized = String(type ?? "").trim().toLowerCase();
    if (normalized === "percentage") {
      return numValue > 100;
    }
    if (normalized === "amount" || normalized === "£" || normalized === "fixed") {
      const unitPrice = resolveDiscountUnitPrice();
      return unitPrice > 0 && numValue > unitPrice;
    }
    return false;
  };

  const getDiscountValidationMessage = (type = discountType) => {
    const normalized = String(type ?? "").trim().toLowerCase();
    if (normalized === "percentage") {
      return "Discount cannot exceed 100%";
    }
    const unitPrice = resolveDiscountUnitPrice();
    return `Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`;
  };

  const requireDiscountCommentIfNeeded = (discountValue) => {
    const num = parseFloat(discountValue);
    if (!Number.isFinite(num) || num <= 0) return true;
    if (!onDiscountCommentChange) return true;
    if (getCartLineDiscountComment(item)) return true;
    const next = window.prompt("Comment is required for discount", "");
    if (next === null) return false;
    const comment = String(next).trim();
    if (!comment) {
      if (onValidationError) {
        onValidationError("Comment is required when discount is applied");
      }
      return false;
    }
    onDiscountCommentChange(item, comment);
    return true;
  };

  const rejectDiscountWithoutComment = () => {
    setDiscountInput("");
    discountInputDraftRef.current = "";
    setDiscountError(true);
    onDiscountChange(item, "0", discountType);
    if (onDiscountChangeComplete) {
      onDiscountChangeComplete(item, "0", discountType);
    }
  };

  const commitDiscountChange = (draft, type = discountType) => {
    if (retainsCartDiscountFocus && isIncompleteDiscountDraft(draft)) return;
    const numValue = parseFloat(draft);
    if (isDiscountValueInvalid(numValue, type)) {
      setDiscountError(true);
      if (onValidationError) {
        onValidationError(getDiscountValidationMessage(type));
      }
      return;
    }
    onDiscountChange(item, draft, type);
  };

  useEffect(() => {
    if (retainsCartDiscountFocus && isDiscountInputFocusedRef.current) return;
    const currentDiscount = (() => {
      const manualDiscount = item.manualOverrides?.discount;
      if (manualDiscount !== undefined && manualDiscount !== null && manualDiscount !== "") {
        const value = parseFloat(manualDiscount);
        return Number.isFinite(value) && value >= 0 ? value : 0;
      }

      if (!isEditMode && item.productWiseDiscountValue !== undefined && item.productWiseDiscountValue !== null && item.productWiseDiscountValue !== "") {
        const value = parseFloat(item.productWiseDiscountValue);
        return Number.isFinite(value) && value >= 0 ? value : 0;
      }

      const apiItemDiscount = item.item_discount !== undefined && item.item_discount !== null && item.item_discount !== "" ? parseFloat(item.item_discount) : null;
      if (apiItemDiscount !== null && Number.isFinite(apiItemDiscount) && apiItemDiscount >= 0) {
        return apiItemDiscount;
      }
      const value = parseFloat(item.discount || 0);
      return Number.isFinite(value) && value >= 0 ? value : 0;
    })();
    if (prevDiscountRef.current !== currentDiscount) {
      const newDiscount = currentDiscount === 0 ? "" : String(currentDiscount);
      setDiscountInput(newDiscount);
      discountInputDraftRef.current = newDiscount;
      prevDiscountRef.current = currentDiscount;
    }
  }, [item.discount, item.item_discount, item.manualOverrides?.discount, item.productWiseDiscountValue, discount, isEditMode]);

  useEffect(() => {
    return () => {
      if (discountDebounceTimerRef.current) {
        clearTimeout(discountDebounceTimerRef.current);
        discountDebounceTimerRef.current = null;
      }
      if (priceDebounceTimerRef.current) {
        clearTimeout(priceDebounceTimerRef.current);
        priceDebounceTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isKeyboardHighlighted || !tableRowRef.current) return;
    tableRowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [isKeyboardHighlighted]);

  const handlePriceInputChange = (event) => {
    const value = event.target.value;

    if (value === "") {
      setPriceInput("");
      setPriceError(false);
      return;
    }

    if (value === ".") {
      setPriceInput(value);
      setPriceError(false);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue <= 99999.99) {
      setPriceInput(value);
      setPriceError(false);
    } else if (numValue > 99999.99) {
      setPriceError(true);
      if (onValidationError) {
        onValidationError("Price must be between £0 and £99,999.99");
      }
    }
  };

  const handlePriceInputBlur = () => {
    isPriceInputFocusedRef.current = false;
    if (priceDebounceTimerRef.current) {
      clearTimeout(priceDebounceTimerRef.current);
      priceDebounceTimerRef.current = null;
    }
    let trimmedValue = priceInput.trim();

    if (trimmedValue === "" || trimmedValue === ".") {
      setPriceInput("1");
      setPriceError(false);
      onPriceChange(item, "1", { commit: true });
      if (isEditMode && onPriceChangeComplete && isPriceEditableFromSettingsLoaded && isPriceEditableFromSettings) onPriceChangeComplete();
      if (onValidationError) {
        onValidationError("Price cannot be empty");
      }
      return;
    }

    const parsed = parseFloat(trimmedValue);

    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 99999.99) {
      setPriceError(true);
      const resetValue = getPriceDisplayValue();
      setPriceInput(resetValue);
      setPriceError(false);
      onPriceChange(item, resetValue, { commit: true });
      if (isEditMode && onPriceChangeComplete && isPriceEditableFromSettingsLoaded && isPriceEditableFromSettings) onPriceChangeComplete();
      if (onValidationError) {
        onValidationError("Price must be between £0 and £99,999.99");
      }
      return;
    }

    const formattedValue = parsed.toFixed(2);
    const result = onPriceChange(item, formattedValue, { commit: true });

    if (result === false) {
      setPriceError(true);
      const resetValue = getPriceDisplayValue();
      setPriceInput(resetValue);
      setPriceError(false);
      if (onValidationError) {
        onValidationError("Please enter a valid price");
      }
      return;
    }

    setPriceInput(formattedValue);
    setPriceError(false);
    if (isEditMode && onPriceChangeComplete && isPriceEditableFromSettingsLoaded && isPriceEditableFromSettings) onPriceChangeComplete();
  };

  const getLiveDiscountForTotal = () => {
    if (discountInput === "" || discountInput === null || discountInput === undefined) {
      return 0;
    }
    const parsed = parseFloat(String(discountInput).trim());
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : discount;
  };

  const lineQtyForTotal = isEditMode ? parseQty(item, allotQty) : qty;

  const calculateTotal = () => {
    const netTotal = resolveCartLineDisplayTotal(item, lineQtyForTotal, {
      discount: getLiveDiscountForTotal(),
      discountType,
      isEditMode,
    });
    return resolveCartLineInlinePromoGrossDisplayTotal(netTotal);
  };

  const vatPercentDisplay = `${Number.isFinite(vatRate) ? vatRate.toFixed(2) : "0.00"}%`;
  const vatAmountDisplay = `£${vatAmount.toFixed(2)}`;
  const taxCodeDisplay = resolveProductTaxCode(item) || "-";
  const vatDisplayValue = isQuickOrder
    ? taxCodeDisplay
    : showVatBoth
      ? `${vatAmountDisplay}`
      : showVatAsAmount
        ? vatAmountDisplay
        : vatPercentDisplay;

  const allotFieldMax = isEditMode
    ? getQtyControlMaxFromBuckets(item, "allot", qtyControlBucketItems)
    : allowDecimalOnRow
      ? 9999.99
      : 9999;

  const revertAllotInput = () => {
    setAllotQtyInput(String(allotQty));
    setAllotQtyError(false);
  };

  const commitAllotToParent = (value) => {
    if (!onAllotQtyChange) {
      return true;
    }
    const accepted = onAllotQtyChange(item, value) !== false;
    if (!accepted) {
      revertAllotInput();
      return false;
    }
    return true;
  };

  const handleAllotQtyInputChange = (event) => {
    const value = event.target.value;

    if (value === "") {
      setAllotQtyInput(value);
      setAllotQtyError(false);
      return;
    }

    if (value === ".") {
      if (!allowDecimalOnRow) {
        setAllotQtyError(true);
        if (onValidationError) {
          onValidationError("Decimal quantity is only allowed for MEATS category");
        }
        return;
      }
      setAllotQtyInput(value);
      setAllotQtyError(false);
      return;
    }

    if (!allowDecimalOnRow && String(value).includes(".")) {
      setAllotQtyError(true);
      if (onValidationError) {
        onValidationError("Decimal quantity is only allowed for MEATS category");
      }
      return;
    }

    const numValue = parseFloat(value);
    const formatMax = allowDecimalOnRow ? 9999.99 : 9999;
    if (isNaN(numValue) || numValue < 0) {
      return;
    }

    if (numValue > formatMax) {
      setAllotQtyError(true);
      if (onValidationError) {
        onValidationError(`Allot Qty cannot exceed ${formatMax}`);
      }
      return;
    }

    setAllotQtyInput(value);
    setAllotQtyError(false);
  };

  const commitAllotQtyFromInput = (rawValue) => {
    const value = String(rawValue ?? "").trim();

    if (value === "" || value === ".") {
      setAllotQtyInput(String(qty));
      setAllotQtyError(false);
      commitAllotToParent(String(qty));
      return;
    }

    let numValue = parseFloat(value);

    if (!Number.isFinite(numValue) || numValue < 0) {
      setAllotQtyError(false);
      revertAllotInput();
      if (onValidationError) {
        onValidationError("Please enter a valid allot quantity");
      }
      return;
    }

    const formatMax = allowDecimalOnRow ? 9999.99 : 9999;
    if (!allowDecimalOnRow && numValue % 1 !== 0) {
      numValue = Math.round(numValue);
    }

    if (numValue > formatMax) {
      setAllotQtyError(true);
      revertAllotInput();
      if (onValidationError) {
        onValidationError(`Allot Qty cannot exceed ${formatMax}`);
      }
      return;
    }

    const formattedValue = allowDecimalOnRow
      ? Number(numValue).toFixed(2)
      : String(Math.round(numValue));
    setAllotQtyInput(formattedValue);
    setAllotQtyError(false);
    commitAllotToParent(formattedValue);
  };

  const handleAllotQtyInputBlur = () => {
    commitAllotQtyFromInput(allotQtyInput);
  };

  const handleAllotQtyInputKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    commitAllotQtyFromInput(event.target.value);
    event.target.blur();
  };

  const closeCellEditor = (cell) => {
    setEditingCell((current) => (current === cell ? null : current));
  };

  const handleDiscountTypeSelect = (event) => {
    const newType = event.target.value;
    const oldType = discountType;
    if (oldType === newType) return;
    if (discountDebounceTimerRef.current) {
      clearTimeout(discountDebounceTimerRef.current);
      discountDebounceTimerRef.current = null;
    }
    const currentValue = discountInput;
    const isEmptyOrZero = !currentValue || currentValue.trim() === "" || currentValue === "0";
    if (!isEmptyOrZero) {
      const numValue = parseFloat(currentValue);
      if (isDiscountValueInvalid(numValue, newType)) {
        setDiscountError(true);
        if (onValidationError) {
          onValidationError(getDiscountValidationMessage(newType));
        }
        return;
      }
    }
    setDiscountType(newType);
    prevDiscountTypeRef.current = newType;
    userChangedDiscountTypeRef.current = true;
    setDiscountInput(currentValue);
    setDiscountError(false);
    if (!isEmptyOrZero) {
      if (!requireDiscountCommentIfNeeded(currentValue)) {
        rejectDiscountWithoutComment();
        return;
      }
      onDiscountChange(item, currentValue, newType);
      if (onDiscountChangeComplete) {
        onDiscountChangeComplete(item, currentValue, newType);
      }
    }
  };

  if (isEditMode) {
    return (
      <>
      <TableRow ref={tableRowRef} hover sx={{
        opacity: isLoading ? 0.5 : 1,
        '& .MuiTableCell-root': {
          fontSize: '0.75rem',
          p: 0.6,
        },
        ...(shouldShowRedText && {
          color: 'error.main',
          '& .MuiTypography-root': {
            color: 'error.main'
          },
          '& .MuiInputBase-input': {
            color: 'error.main'
          }
        }),
        ...keyboardHighlightSx,
      }}>
        <TableCell align="center">
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
            {index + 1}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" fontWeight={600} fontSize="0.75rem">
              {code}
            </Typography>
            {promoIndicatorLetter === "F" && (
              <Typography
                fontWeight={600}
                color="info.darker"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: '0.65rem',
                }}
              >
                <Iconify icon="solar:check-circle-bold" width={12} />
                (F)
              </Typography>
            )}
            {promoIndicatorLetter === "P" && (
              <Typography
                fontWeight={500}
                color="info.darker"
                fontSize="0.65rem"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Iconify icon="solar:check-circle-bold" width={12} />
                (P)
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell >
          {/* {canSplit ? (
            <Select
              value={orderType}
              onChange={(e) => onTypeChange(item, e.target.value)}
              size="small"
              disabled={isDisabled}
              sx={{ 
                '& .MuiSelect-select': { 
                  fontSize: '0.7rem', 
                  py: 0.2,
                  px: 1,
                  textAlign: 'left',
                  textOverflow: 'unset',
                  overflow: 'visible',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: 'none',
                  height: 25,
                  width: 35,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#000'
                }
              }}
            >
              <MenuItem value="Box" sx={{ fontSize: '0.75rem' }}>Box</MenuItem>
              <MenuItem value="Piece" sx={{ fontSize: '0.75rem' }}>Piece</MenuItem>
            </Select>
          ) : ( */}
          <Typography variant="caption" fontWeight={600}>
            {displayOrderType}
          </Typography>
          {/* )} */}
        </TableCell>
        <TableCell sx={{ maxWidth: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                fontSize="0.75rem"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                  ...(shouldShowRedText
                    ? { color: 'error.main' }
                    : isPromoFreeOrGetProductName
                      ? { color: '#0456f1' }
                      : {}),
                }} 
                title={description}
              >
                {description}
              </Typography>
              {isContractPrice && (
                <Chip
                  label="C"
                  size="small"
                  color="primary"
                  sx={{ height: 12, fontSize: '0.65rem', fontWeight: 600 }}
                />
              )}
            </Box>
            {showPromotionNameInlineUnderDescription ? (
              <Typography
                variant="caption"
                fontWeight={500}
                color="#0456f1"
                sx={{
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                }}
                title={promotionNameTrimmed}
              >
                {promotionNameTrimmed}
              </Typography>
            ) : null}
            {discountCommentTrimmed ? (
              <Typography
                variant="caption"
                fontWeight={400}
                color="text.secondary"
                sx={{
                  fontSize: '0.7rem',
                  fontStyle: 'italic',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={discountCommentTrimmed}
              >
                {discountCommentTrimmed}
              </Typography>
            ) : null}
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography fontSize="0.7rem" color="inherit" fontWeight={500}>
            £{originalPrice > 0 ? originalPrice.toFixed(2) : currentPrice.toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography fontSize="0.7rem" color="inherit" fontWeight={500}>
            {qty}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {editingCell === "allot" ? (
          <TextField
            autoFocus
            value={allotQtyInput}
            onFocus={(e) => {
              isAllotQtyInputFocusedRef.current = true;
              e.target.select();
            }}
            onWheel={(e) => e.target.blur()}
            onChange={handleAllotQtyInputChange}
            onBlur={() => {
              isAllotQtyInputFocusedRef.current = false;
              handleAllotQtyInputBlur();
              closeCellEditor("allot");
            }}
            onKeyDown={handleAllotQtyInputKeyDown}
            size="small"
            fullWidth

            disabled={isDisabled}
            error={allotQtyError}
            type="number"
            inputProps={{
              step: allowDecimalOnRow ? "0.01" : "1",
              min: allowDecimalOnRow ? "0.01" : "0",
              max: allotFieldMax,
              style: { textAlign: 'center' },
            }}
            sx={{
              width: 70,
              '& .MuiInputBase-root': {
                height: 26,
                display: 'flex',
                justifyContent: 'center',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#000',
                },
              },
              '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                opacity: 1
              },

            }}
          />
          ) : (
            <CartClickToEditValue
              value={allotQtyInput === "" ? String(qty) : allotQtyInput}
              disabled={isDisabled}
              width={70}
              error={allotQtyError}
              bordered
              onStartEdit={() => setEditingCell("allot")}
            />
          )}
        </TableCell>
        <TableCell align="right">
          {(() => {
            const showPriceInput =
              isEditMode &&
              isPriceEditableFromSettingsLoaded &&
              isPriceEditableFromSettings &&
              !isItemPromoted &&
              !showOriginalPrice &&
              !isFreeItem;

            if (showPriceInput) {
              if (editingCell !== "price") {
                const numValue = parseFloat(priceInput);
                const displayPrice = Number.isFinite(numValue) ? numValue.toFixed(2) : "0.00";
                return (
                  <CartClickToEditValue
                    value={`£${displayPrice}`}
                    disabled={isPriceDisabled}
                    width={70}
                    align="right"
                    error={priceError}
                    onStartEdit={() => setEditingCell("price")}
                  />
                );
              }
              return (
                <TextField
                  autoFocus
                  value={priceInput}
                  onFocus={(e) => {
                    isPriceInputFocusedRef.current = true;
                    e.target.select();
                  }}
                  onWheel={(e) => e.target.blur()}
                  onChange={handlePriceInputChange}
                  onBlur={() => {
                    handlePriceInputBlur();
                    closeCellEditor("price");
                  }}
                  size="small"
                  disabled={isPriceDisabled}
                  error={priceError}
                  type="number"
                  inputProps={{
                    step: "0.01",
                    min: "0",
                    style: { textAlign: 'center', fontSize: '0.75rem' }
                  }}
                  sx={{
                    width: 70,
                    '& .MuiInputBase-root': {
                      height: 26,
                      display: 'flex',
                      justifyContent: 'center',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000',
                      },
                    },
                    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                      opacity: 1
                    },
                  }}
                />
              );
            }

            return (() => {
              const hasPromoPrice = item.promo_unit_price !== undefined && item.promo_unit_price !== null;
              const hasUnitPrice = item.unit_price !== undefined && item.unit_price !== null;
              let displayPrice = 0;
              let originalPrice = null;
              let shouldShowStrikethrough = false;

              if (item.useBackendCalculations) {
                if (hasPromoPrice) {
                  const promoPrice = parseFloat(item.promo_unit_price);
                  const strikePrice = resolveCartLineStrikethroughPrice(item);
                  displayPrice = promoPrice;

                  if (strikePrice > 0.01 && promoPrice < strikePrice - 0.01) {
                    originalPrice = strikePrice;
                    shouldShowStrikethrough = true;
                  }
                } else if (hasUnitPrice) {
                  displayPrice = parseFloat(item.unit_price);
                }
              } else {
                const numValue = parseFloat(priceInput);
                displayPrice = Number.isFinite(numValue) ? numValue : 0;
              }

              if (shouldShowStrikethrough && originalPrice !== null) {
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                    <Typography
                      fontSize="0.7rem"
                      color="inherit"
                      sx={{
                        textDecoration: 'line-through',
                        opacity: 0.6,
                        fontSize: '0.65rem',
                        textAlign: 'right'
                      }}
                    >
                      £{originalPrice.toFixed(2)}
                      
                    </Typography>
                    <Typography fontSize="0.7rem" color="inherit" fontWeight={500} sx={{ textAlign: 'right' }}>
                      £{displayPrice.toFixed(2)}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Typography fontSize="0.7rem" color="inherit" fontWeight={500}>
                  £{displayPrice.toFixed(2)}
                </Typography>
              );
            })();
          })()}
        </TableCell>
        <TableCell align="center">
          <CartDiscountHtmlField
            value={isDiscountDisabled ? "" : discountInput}
            discountType={discountType}
            disabled={isDiscountDisabled}
            error={discountError}
            height={26}
            onFocus={(e) => e.target.select()}
            onTypeChange={handleDiscountTypeSelect}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "" || value === ".") {
                setDiscountInput(value);
                setDiscountError(false);
                onDiscountChange(item, "0", discountType);
                return;
              }

              if (discountType === "percentage") {
                const numericValue = value.replace(/[^0-9.]/g, '');
                const numValue = parseFloat(numericValue);

                if (!isNaN(numValue) && numValue > 100) {
                  setDiscountError(true);
                  if (onValidationError) {
                    onValidationError("Discount cannot exceed 100%");
                  }
                  return;
                }

                setDiscountInput(numericValue);
                setDiscountError(false);

                if (!isNaN(numValue) && numValue >= 0) {
                  onDiscountChange(item, numericValue, discountType);
                }
              } else {
                const numValue = parseFloat(value);

                if (!isNaN(numValue) && numValue >= 0) {
                  if (isDiscountValueInvalid(numValue, discountType)) {
                    setDiscountError(true);
                    if (onValidationError) {
                      onValidationError(getDiscountValidationMessage(discountType));
                    }
                    return;
                  }

                  setDiscountInput(value);
                  setDiscountError(false);
                  onDiscountChange(item, value, discountType);
                } else {
                  setDiscountInput(value);
                  setDiscountError(true);
                }
              }
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();

              if (value === "" || value === ".") {
                setDiscountInput("");
                setDiscountError(false);
                onDiscountChange(item, "0", discountType);
                if (isEditMode && onDiscountChangeComplete) {
                  onDiscountChangeComplete(item, "0", discountType);
                }
                return;
              }

              const numValue = parseFloat(value);
              const maxValue = discountType === "percentage" ? 100 : undefined;

              if (!Number.isFinite(numValue) || numValue < 0 || (maxValue !== undefined && numValue > maxValue)) {
                const currentDiscountValue = discount === 0 ? "" : String(discount);
                setDiscountInput(currentDiscountValue);
                setDiscountError(true);
                if (onValidationError) {
                  onValidationError(`Please enter a valid discount ${discountType === "percentage" ? "percentage" : "amount"}`);
                }
                return;
              }

              const decimalPlaces = (value.split('.')[1] || '').length;
              if (decimalPlaces > 2) {
                const currentDiscountValue = discount === 0 ? "" : String(discount);
                setDiscountInput(currentDiscountValue);
                setDiscountError(true);
                if (onValidationError) {
                  onValidationError("Discount can have maximum 2 decimal places");
                }
                return;
              }

              const isFixedDiscount = discountType === "amount" || discountType === "£" || discountType === "fixed";
              if (isFixedDiscount && Number.isFinite(numValue) && numValue >= 0) {
                if (isDiscountValueInvalid(numValue, discountType)) {
                  const currentDiscountValue = discount === 0 ? "" : String(discount);
                  setDiscountInput(currentDiscountValue);
                  setDiscountError(true);
                  if (onValidationError) {
                    onValidationError(getDiscountValidationMessage(discountType));
                  }
                  return;
                }
              }

              const formattedValue = parseFloat(value).toString();
              const formattedNum = parseFloat(formattedValue);
              const currentDiscountNum = parseFloat(discount);
              const isExplicitZero = formattedNum === 0;
              const hasChanged = Math.abs(formattedNum - currentDiscountNum) >= 0.01;

              if (!isExplicitZero && Number.isFinite(formattedNum) && Number.isFinite(currentDiscountNum) && !hasChanged) {
                if (formattedNum > 0 && !requireDiscountCommentIfNeeded(formattedValue)) {
                  rejectDiscountWithoutComment();
                  return;
                }
                setDiscountInput(formattedValue);
                setDiscountError(false);
                if (isEditMode && onDiscountChangeComplete) {
                  onDiscountChangeComplete(item, formattedValue, discountType);
                }
                return;
              }

              if (formattedNum > 0 && !requireDiscountCommentIfNeeded(formattedValue)) {
                rejectDiscountWithoutComment();
                return;
              }

              setDiscountInput(formattedValue);
              setDiscountError(false);
              onDiscountChange(item, formattedValue, discountType);

              if (isEditMode && onDiscountChangeComplete) {
                onDiscountChangeComplete(item, formattedValue, discountType);
              }
            }}
          />
          {onDiscountCommentChange ? (
            <IconButton
              size="small"
              title={discountCommentTrimmed || "Add discount comment"}
              onClick={() => {
                const next = window.prompt(
                  parseFloat(discount) > 0 ? "Comment is required for discount" : "Discount comment",
                  discountCommentTrimmed
                );
                if (next === null) return;
                const comment = String(next).trim();
                if (!comment && parseFloat(discount) > 0) {
                  if (onValidationError) {
                    onValidationError("Comment is required when discount is applied");
                  }
                  return;
                }
                onDiscountCommentChange(item, next);
              }}
              sx={{ padding: '2px', marginLeft: '2px' }}
            >
              <Iconify
                icon={discountCommentTrimmed ? "mdi:comment-text" : "mdi:comment-outline"}
                width={16}
                height={16}
                sx={{ color: discountCommentTrimmed ? '#0456f1' : 'text.disabled' }}
              />
            </IconButton>
          ) : null}
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Typography fontSize="0.7rem" color="inherit" fontWeight={500}>
              £{calculateTotal().toFixed(2)}
            </Typography>
            {showInlinePromoDiscount ? (
              <Typography fontSize="0.7rem" fontWeight={600} sx={{ color: '#0456f1' }}>
                -£{inlinePromoDiscountNet.toFixed(2)}
              </Typography>
            ) : null}
          </Box>
        </TableCell>
        <TableCell sx={{ minWidth: 80, whiteSpace: 'nowrap' }} align="center">
          <Typography fontSize="0.7rem" color="inherit" fontWeight={500}>
            {vatPercentDisplay}
          </Typography>
        </TableCell>
      

        <TableCell sx={{ textAlign: 'center' }}>
          <IconButton
            onClick={() => onDelete(item.id)}
            sx={{ color: "error.main" }}
            size="small"
            disabled={isDisabled}
          >
                 <Iconify icon="mdi:close" width={18} height={18}  />
          </IconButton>
        </TableCell>
      </TableRow>
      {promotionSummarySubRowCount > 0
        ? Array.from({ length: promotionSummarySubRowCount }, (_, promoRowIndex) => (
        <TableRow
          key={`${item.id || index}-promo-summary-${promoRowIndex}`}
          data-cart-row={index}
          sx={{
            '& .MuiTableCell-root': {
              p: 0.5,
              fontSize: '0.7rem',
              borderTop: 0,
            },
            ...keyboardHighlightSx,
          }}
        >
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell sx={{ maxWidth: 200 }}>
            <Typography
              variant="caption"
              fontWeight={500}
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1.2,
                color: '#0456f1',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                textTransform: 'uppercase',
              }}
              title={promotionNameTrimmed}
            >
              {promotionNameTrimmed}
            </Typography>
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell align="right">
            <Typography fontSize="0.7rem" fontWeight={600} sx={{ color: '#0456f1' }}>
              -£{promotionSummaryUnitDiscount.toFixed(2)}
            </Typography>
          </TableCell>
          <TableCell />
          <TableCell />
        </TableRow>
      ))
        : null}
      </>
    );
  }

  return (
    <>
    <TableRow
      ref={tableRowRef}
      hover
      data-cart-row={index}
      onClick={() => onQtyRegionFocusChange?.(index)}
      sx={{
      '& .MuiTableCell-root': {
        p: 0.7,
        fontSize: '0.7rem'
      },
      opacity: isLoading ? 0.5 : 1,
      ...(shouldShowRedText && {
        color: 'error.main',
        '& .MuiTypography-root': {
          color: 'error.main'
        },
        '& .MuiInputBase-input': {
          color: 'error.main'
        }
      }),
      ...keyboardHighlightSx,
    }}
    >
      <TableCell align="center">
        <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
          {index + 1}
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
            {code}
          </Typography>
          {promoIndicatorLetter === "F" && (
            <Typography
              fontSize="0.65rem"
              fontWeight={500}
              color="info.darker"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5
              }}
            >
              <Iconify icon="solar:check-circle-bold" width={12} />
              (F)
            </Typography>
          )}
          {promoIndicatorLetter === "P" && (
            <Typography
              fontSize="0.65rem"
              fontWeight={500}
              color="info.darker"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5
              }}
            >
              <Iconify icon="solar:check-circle-bold" width={12} />
              (P)
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ maxWidth: 200 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                flex: 1,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 600,
                ...(shouldShowRedText
                  ? { color: 'error.main !important' }
                  : isPromoFreeOrGetProductName
                    ? { color: '#0456f1 !important' }
                    : { color: '#000 !important' }),
              }}
              title={description}
            >
              {description}
            </Typography>
            {isContractPrice && (
              <Chip
                label="C"
                size="small"
                color="primary"
                sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
              />
            )}

          </Box>
          {showPromotionNameInlineUnderDescription ? (
            <Typography
              variant="caption"
              fontWeight={500}
              color="#0456f1"
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
              title={promotionNameTrimmed}
            >
              {promotionNameTrimmed}
            </Typography>
          ) : null}
          {discountCommentTrimmed ? (
            <Typography
              variant="caption"
              fontWeight={400}
              color="text.secondary"
              sx={{
                fontSize: '0.7rem',
                fontStyle: 'italic',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={discountCommentTrimmed}
            >
              {discountCommentTrimmed}
            </Typography>
          ) : null}
        </Box>
      </TableCell>
      {!hideTypeColumn ? (
      <TableCell sx={{ verticalAlign: 'middle', minWidth: 35 }}>
        {/* {canSplit ? (
          <Select
            value={orderType}
            onChange={(e) => onTypeChange(item, e.target.value)}
            size="small"
            disabled={isDisabled}
    
            sx={{ 
              '& .MuiSelect-select': { 
                fontSize: '0.7rem', 
                py: 0.2,
                px: 1,
                textAlign: 'left',
                textOverflow: 'unset',
                overflow: 'visible',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                maxWidth: 'none',
                height: 24,
                width: 35,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#000'
              }
            }}
          >
            <MenuItem value="Box" sx={{ fontSize: '0.7rem' }}>Box</MenuItem>
            <MenuItem value="Piece" sx={{ fontSize: '0.7rem' }}>Piece</MenuItem>
          </Select>
        ) : ( */}
        <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
          {displayOrderType}
        </Typography>
        {/* )} */}
      </TableCell>
      ) : null}

      <TableCell
        align="center"
        data-cart-qty-scope={index}
        data-qty-product-id={String(item.product_id || item.id || "")}
        data-qty-offer-line={isOfferLineDisplay ? "1" : undefined}
        onFocusCapture={
          typeof onQtyRegionFocusChange === "function"
            ? () => onQtyRegionFocusChange(index)
            : undefined
        }
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <QuantityControl
            isEditMode={isEditMode}
            value={qty}
            onChange={(newQty) => {
              if (!onQuantityChange) return true;
              return onQuantityChange(item, newQty, 'absolute') !== false;
            }}
            onIncrement={(delta) => onQuantityChange(item, delta, 'increment')}
            onDecrement={(delta) => onQuantityChange(item, delta, 'decrement')}
            min={allowDecimalOnRow ? 0.01 : 1}
            max={qtyControlMax}
            size="small"
            disabled={isDisabled}
            autoOpen={autoOpenQuantityPopup}
            autoFocusInput={autoFocusQuantityInput}
            keepFocusOnEnter={Boolean(isTelecaller && isQuickOrder && PREFIX !== "rhp" && !isRhp)}
            preferInlineQty={Boolean(isQuickOrder && (isTelecaller || PREFIX === "rhp" || isRhp))}
            inlineEditKey={quantityInlineEditKey}
            onPopupClose={onQuantityPopupClose}
            onPopupOpen={onQuantityPopupOpen}
            isQuickOrder={isQuickOrder}
            orderType={orderType}
            canSplit={isQuickOrder ? canSplit : false}
            onOrderTypeChange={(newOrderType) => onTypeChange(item, newOrderType)}
            onAddAsNewType={onAddAsNewType ? (newType, qty) => onAddAsNewType(item, newType, qty) : undefined}
            allowDecimal={allowDecimalOnRow}
            isPromoTypeFree={isOfferLineDisplay && isPromoTypeFree}
            promoAlternateQtyButtons={promoAlternateQtyButtons}
            onPromoPButtonClick={
              promoAlternateQtyButtons && typeof onPromoQtyLineOpenDrawer === "function"
                ? () => onPromoQtyLineOpenDrawer(item)
                : undefined
            }
            onPromoCPButtonClick={
              promoAlternateQtyButtons && typeof onPromoQtyOpenPromotionsModal === "function"
                ? () => onPromoQtyOpenPromotionsModal(item)
                : undefined
            }
          />
        </Box>
      </TableCell>
      {!hideBoxPriceColumn ? (
      <TableCell align="right">
        <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ textAlign: 'right' }}>
          {(() => {
            const standardPrice = orderType === "Piece"
              ? (item.sales_piece_price !== undefined && item.sales_piece_price !== null && item.sales_piece_price !== "" ? parseFloat(item.sales_piece_price) : 0)
              : (item.sales_box_price !== undefined && item.sales_box_price !== null && item.sales_box_price !== "" ? parseFloat(item.sales_box_price) : 0);
            return `£${standardPrice.toFixed(2)}`;
          })()}
        </Typography>
      </TableCell>
      ) : null}
      <TableCell align="right">
        {rhpReadOnlyPricing ? (
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ textAlign: "right" }}>
            £{rhpListPrice.toFixed(2)}
          </Typography>
        ) : allowEveUnitPriceEdit ? (
          editingCell === "unitPrice" ? (
          <TextField
            autoFocus
            value={unitPriceInput}
            onFocus={(e) => {
              unitPriceInputFocusedRef.current = true;
              e.target.select();
            }}
            onBlur={() => {
              unitPriceInputFocusedRef.current = false;
              const trimmed = unitPriceInput.trim();
              if (trimmed === "" || trimmed === ".") {
                const fallback = getUnitPriceDisplayValue() || "0.00";
                setUnitPriceInput(fallback);
                setUnitPriceError(false);
                justCommittedUnitPriceRef.current = true;
                onPriceChange(item, fallback, { commit: true });
                closeCellEditor("unitPrice");
                return;
              }
              const parsed = parseFloat(trimmed);
              if (!Number.isFinite(parsed) || parsed < 0 || parsed > 99999.99) {
                setUnitPriceError(true);
                const reset = getUnitPriceDisplayValue();
                setUnitPriceInput(reset);
                setUnitPriceError(false);
                justCommittedUnitPriceRef.current = true;
                onPriceChange(item, reset, { commit: true });
                if (onValidationError) onValidationError("Price must be between £0 and £99,999.99");
                closeCellEditor("unitPrice");
                return;
              }
              const formatted = parsed.toFixed(2);
              setUnitPriceInput(formatted);
              setUnitPriceError(false);
              justCommittedUnitPriceRef.current = true;
              onPriceChange(item, formatted, { commit: true });
              closeCellEditor("unitPrice");
            }}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || value === ".") {
                setUnitPriceInput(value);
                setUnitPriceError(false);
                onPriceChange(item, value, { commit: false });
                return;
              }
              const num = parseFloat(value);
              if (!isNaN(num) && num <= 99999.99) {
                setUnitPriceInput(value);
                setUnitPriceError(false);
                onPriceChange(item, value, { commit: false });
              } else if (num > 99999.99) {
                setUnitPriceError(true);
                if (onValidationError) onValidationError("Price must be between £0 and £99,999.99");
              }
            }}
            size="small"
            error={unitPriceError}
            type="number"
            inputProps={{
              step: "0.01",
              min: "0",
              max: "99999.99",
              style: { textAlign: "right", fontSize: "0.6rem" },
            }}

            sx={{
              width: 80,
              "& .MuiInputBase-root": { height: 27 },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#000000 !important",
              },
            }}
            disabled={isPriceDisabled}
          />
          ) : (
            <CartClickToEditValue
              value={unitPriceInput ? `£${unitPriceInput}` : "£0.00"}
              disabled={isPriceDisabled}
              width={80}
              align="right"
              error={unitPriceError}
              onStartEdit={() => setEditingCell("unitPrice")}
            />
          )
        ) : (
          (() => {
            const hasPromoPrice =
              item.promo_unit_price !== undefined &&
              item.promo_unit_price !== null &&
              item.promo_unit_price !== "";
            const hasUnitPrice =
              item.unit_price !== undefined &&
              item.unit_price !== null &&
              item.unit_price !== "";
            const itemUnitPrice = resolveCartLineStrikethroughPrice(item);
            const itemPrice = parseFloat(item.price || currentPrice || 0);
            const isPromotedLine =
              item.is_promoted === "1" || item.is_promoted === 1 || item.is_promoted === true;
            const hasManualDiscount = (() => {
              const manualDiscount = item.manualOverrides?.discount;
              const productWiseDiscount = item.productWiseDiscountValue || item.discount;
              const hasManualOverride =
                manualDiscount !== undefined &&
                manualDiscount !== null &&
                parseFloat(manualDiscount) > 0;
              const hasProductWiseDiscount =
                productWiseDiscount !== undefined &&
                productWiseDiscount !== null &&
                parseFloat(productWiseDiscount) > 0;
              return hasManualOverride || hasProductWiseDiscount;
            })();

            let displayPrice = itemPrice;
            if (hasPromoPrice) {
              const promoPrice = parseFloat(item.promo_unit_price);
              if (isPromoTypeFree) {
                displayPrice = Number.isFinite(promoPrice) ? promoPrice : 0;
              } else {
                displayPrice = promoPrice;
              }
            } else if (hasUnitPrice) {
              displayPrice = parseFloat(item.unit_price);
            }
            if (!isPromoTypeFree && !(isOfferLineDisplay && (isGetPromoType || isPromoTypeFree))) {
              const resolvedOfferOrSales = resolveOfferOrSalesUnitPrice(item, orderType);
              if (displayPrice <= 0.0001 && resolvedOfferOrSales > 0.0001) {
                displayPrice = resolvedOfferOrSales;
              }
            } else if (isPromoTypeFree) {
              const rawPromoUnit = hasPromoPrice ? parseFloat(item.promo_unit_price) : NaN;
              displayPrice = Number.isFinite(rawPromoUnit) ? rawPromoUnit : 0;
            } else if (
              itemPrice <= 0 &&
              itemUnitPrice > 0 &&
              !hasManualDiscount &&
              !isPromoTypeFree
            ) {
              displayPrice = itemUnitPrice;
            }
            if (hasManualDiscount && !isPromoTypeFree) {
              displayPrice = itemPrice;
            }

            const shouldShowOfferStrikethrough =
              !isPromoTypeBuy &&
              itemUnitPrice > 0.01 &&
              displayPrice < itemUnitPrice - 0.01 &&
              (isPromoTypeFree ||
                isGetPromoType ||
                isFreeItem ||
                isGetPromotion ||
                isOfferLineDisplay ||
                displayPrice < 0.01 ||
                (isPromotedLine && itemPrice < itemUnitPrice) ||
                (hasManualDiscount && itemPrice < itemUnitPrice));

            if (shouldShowOfferStrikethrough) {
              return (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", textAlign: "right", textDecoration: "line-through", opacity: 1 }}
                  >
                    £{itemUnitPrice.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ textAlign: "right" }}>
                    £{displayPrice.toFixed(2)}
                  </Typography>
                </Box>
              );
            }

            return (
              <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ textAlign: "right" }}>
                £{displayPrice.toFixed(2)}
              </Typography>
            );
          })()
        )}
      </TableCell>

      <TableCell align={rhpReadOnlyPricing ? "right" : "center"} {...(retainsCartDiscountFocus && !rhpReadOnlyPricing ? { "data-cart-discount-input": true } : {})}>
        {rhpReadOnlyPricing ? (
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ textAlign: "right" }}>
            £{rhpPriceDiscount.toFixed(2)}
          </Typography>
        ) : (
          <CartDiscountHtmlField
            value={discountInput}
            discountType={discountType}
            disabled={isDiscountDisabled}
            error={discountError}
            dataCartDiscountInput={retainsCartDiscountFocus}
            onFocus={(e) => {
              if (retainsCartDiscountFocus) {
                isDiscountInputFocusedRef.current = true;
              }
              e.target.select();
            }}
            onTypeChange={handleDiscountTypeSelect}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "" || value === ".") {
                if (discountDebounceTimerRef.current) {
                  clearTimeout(discountDebounceTimerRef.current);
                  discountDebounceTimerRef.current = null;
                }
                setDiscountInput(value);
                discountInputDraftRef.current = value;
                setDiscountError(false);
                if (retainsCartDiscountFocus) {
                  if (value === "") {
                    commitDiscountChange("0", discountType);
                  }
                } else {
                  onDiscountChange(item, value, discountType);
                }
                return;
              }

              if (discountType === "percentage") {
                const numericValue = value.replace(/[^0-9.]/g, '');
                const numValue = parseFloat(numericValue);

                if (!isNaN(numValue) && numValue > 100) {
                  setDiscountError(true);
                  if (onValidationError) {
                    onValidationError("Discount cannot exceed 100%");
                  }
                  return;
                }

                setDiscountInput(numericValue);
                discountInputDraftRef.current = numericValue;
                setDiscountError(false);

                if (discountDebounceTimerRef.current) {
                  clearTimeout(discountDebounceTimerRef.current);
                }

                discountDebounceTimerRef.current = setTimeout(() => {
                  if (retainsCartDiscountFocus) {
                    commitDiscountChange(discountInputDraftRef.current, discountType);
                  } else {
                    onDiscountChange(item, numericValue, discountType);
                  }
                  discountDebounceTimerRef.current = null;
                }, 500);
              } else {
                const numValue = parseFloat(value);
                if (!isNaN(numValue) && isDiscountValueInvalid(numValue, discountType)) {
                  setDiscountError(true);
                  if (onValidationError) {
                    onValidationError(getDiscountValidationMessage(discountType));
                  }
                  return;
                }

                setDiscountInput(value);
                discountInputDraftRef.current = value;
                setDiscountError(false);

                if (discountDebounceTimerRef.current) {
                  clearTimeout(discountDebounceTimerRef.current);
                }

                discountDebounceTimerRef.current = setTimeout(() => {
                  if (retainsCartDiscountFocus) {
                    commitDiscountChange(discountInputDraftRef.current, discountType);
                  } else {
                    onDiscountChange(item, value, discountType);
                  }
                  discountDebounceTimerRef.current = null;
                }, 500);
              }

              const numValue = parseFloat(value);

              if (!isNaN(numValue) && numValue >= 0) {
                setDiscountError(false);
              } else {
                setDiscountError(true);
              }
            }}
            onBlur={(e) => {
              if (retainsCartDiscountFocus) {
                isDiscountInputFocusedRef.current = false;
              }
              if (discountDebounceTimerRef.current) {
                clearTimeout(discountDebounceTimerRef.current);
                discountDebounceTimerRef.current = null;
              }

              const value = e.target.value.trim();

              if (value === "" || value === ".") {
                const currentDiscountValue = discount === 0 ? "0" : String(discount);
                if (currentDiscountValue !== "0") {
                  setDiscountInput("");
                  setDiscountError(false);
                  onDiscountChange(item, "0", discountType);
                }
                return;
              }

              const numValue = parseFloat(value);
              const maxValue = discountType === "percentage" ? 100 : undefined;

              if (!Number.isFinite(numValue) || numValue < 0 || (maxValue !== undefined && numValue > maxValue)) {
                const currentDiscountValue = discount === 0 ? "" : String(discount);
                setDiscountInput(currentDiscountValue);
                setDiscountError(false);
                const currentDiscountNum = parseFloat(discount);
                if (!Number.isFinite(currentDiscountNum) || currentDiscountNum !== numValue) {
                  onDiscountChange(item, String(discount), discountType);
                }
                if (onValidationError) {
                  onValidationError(`Please enter a valid discount ${discountType === "percentage" ? "percentage" : "amount"}`);
                }
                return;
              }

              const decimalPlaces = (value.split('.')[1] || '').length;
              if (decimalPlaces > 2) {
                const currentDiscountValue = discount === 0 ? "" : String(discount);
                setDiscountInput(currentDiscountValue);
                setDiscountError(false);
                const currentDiscountNum = parseFloat(discount);
                if (!Number.isFinite(currentDiscountNum) || currentDiscountNum !== numValue) {
                  onDiscountChange(item, String(discount), discountType);
                }
                if (onValidationError) {
                  onValidationError("Discount can have maximum 2 decimal places");
                }
                return;
              }

              const isFixedDiscount = discountType === "amount" || discountType === "£" || discountType === "fixed";
              if (isFixedDiscount && Number.isFinite(numValue) && numValue > 0) {
                if (isDiscountValueInvalid(numValue, discountType)) {
                  const currentDiscountValue = discount === 0 ? "" : String(discount);
                  setDiscountInput(currentDiscountValue);
                  setDiscountError(false);
                  const currentDiscountNum = parseFloat(discount);
                  if (!Number.isFinite(currentDiscountNum) || currentDiscountNum !== numValue) {
                    onDiscountChange(item, String(discount), discountType);
                  }
                  if (onValidationError) {
                    onValidationError(getDiscountValidationMessage(discountType));
                  }
                  return;
                }
              }

              const formattedValue = parseFloat(value).toString();
              const formattedNum = parseFloat(formattedValue);
              const currentDiscountNum = parseFloat(discount);
              const isExplicitZero = formattedNum === 0;

              if (!isExplicitZero && Number.isFinite(formattedNum) && Number.isFinite(currentDiscountNum) &&
                Math.abs(formattedNum - currentDiscountNum) < 0.01) {
                if (formattedNum > 0 && !requireDiscountCommentIfNeeded(formattedValue)) {
                  rejectDiscountWithoutComment();
                  return;
                }
                setDiscountInput(formattedValue);
                setDiscountError(false);
                return;
              }

              if (retainsCartDiscountFocus) {
                setDiscountInput(formattedValue);
                discountInputDraftRef.current = formattedValue;
                setDiscountError(false);
                commitDiscountChange(formattedValue, discountType);
              } else {
                if (formattedNum > 0 && !requireDiscountCommentIfNeeded(formattedValue)) {
                  rejectDiscountWithoutComment();
                  return;
                }
                setDiscountInput(formattedValue);
                discountInputDraftRef.current = formattedValue;
                setDiscountError(false);
                onDiscountChange(item, formattedValue, discountType);
              }
            }}
          />
        )}
        {onDiscountCommentChange ? (
          <IconButton
            size="small"
            title={discountCommentTrimmed || "Add discount comment"}
            onClick={() => {
              const next = window.prompt(
                parseFloat(discount) > 0 ? "Comment is required for discount" : "Discount comment",
                discountCommentTrimmed
              );
              if (next === null) return;
              const comment = String(next).trim();
              if (!comment && parseFloat(discount) > 0) {
                if (onValidationError) {
                  onValidationError("Comment is required when discount is applied");
                }
                return;
              }
              onDiscountCommentChange(item, next);
            }}
            sx={{ padding: '2px', marginLeft: '2px' }}
          >
            <Iconify
              icon={discountCommentTrimmed ? "mdi:comment-text" : "mdi:comment-outline"}
              width={16}
              height={16}
              sx={{ color: discountCommentTrimmed ? '#0456f1' : 'text.disabled' }}
            />
          </IconButton>
        ) : null}
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
            £{calculateTotal().toFixed(2)}
          </Typography>
          {showInlinePromoDiscount ? (
            <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ color: '#0456f1' }}>
              -£{inlinePromoDiscountNet.toFixed(2)}
            </Typography>
          ) : null}
        </Box>
      </TableCell>
      {!hideVatColumn ? (
      <TableCell align="right">
        {enableVatTooltip ? (
          <Tooltip arrow placement="top" title={`VAT: £${vatAmount.toFixed(2)}`}>
            <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
              {vatDisplayValue}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
            {vatDisplayValue}
          </Typography>
        )}
      </TableCell>
      ) : null}

      <TableCell align="center">
        <IconButton
          onClick={() => onDelete(item.id)}
          sx={{ color: "error.main" }}
          size="small"
          disabled={isDisabled}
        >
          <Iconify icon="mdi:close" width={18} height={18}  />
        </IconButton>
      </TableCell>
    </TableRow>
    {promotionSummarySubRowCount > 0
      ? Array.from({ length: promotionSummarySubRowCount }, (_, promoRowIndex) => (
      <TableRow
        key={`${item.id || index}-promo-summary-${promoRowIndex}`}
        data-cart-row={index}
        sx={{
          '& .MuiTableCell-root': {
            p: 0.5,
            fontSize: '0.7rem',
            borderTop: 0,
          },
          ...keyboardHighlightSx,
        }}
      >
        <TableCell />
        <TableCell />
        <TableCell sx={{ maxWidth: 200 }}>
          <Typography
            variant="caption"
            fontWeight={500}
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1.2,
              color: '#0456f1',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textTransform: 'uppercase',
            }}
            title={promotionNameTrimmed}
          >
            {promotionNameTrimmed}
          </Typography>
        </TableCell>
        {!hideTypeColumn ? <TableCell /> : null}
        <TableCell align="center" />
        {!hideBoxPriceColumn ? <TableCell /> : null}
        <TableCell />
        <TableCell />
        <TableCell align="right">
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ color: '#0456f1' }}>
            -£{promotionSummaryUnitDiscount.toFixed(2)}
          </Typography>
        </TableCell>
        {!hideVatColumn ? <TableCell /> : null}
        <TableCell />
      </TableRow>
    ))
      : null}
    </>
  );
};

function areItemDetailsEqual(prev, next) {
  if (prev === next) return true;
  if (!prev && !next) return true;
  if (!prev || !next) return false;
  return (
    Boolean(prev.isFree) === Boolean(next.isFree) &&
    Boolean(prev.isPromoted) === Boolean(next.isPromoted) &&
    Boolean(prev.isGetPromotion) === Boolean(next.isGetPromotion) &&
    Boolean(prev.isDiscounted) === Boolean(next.isDiscounted) &&
    Number(prev.currentPrice) === Number(next.currentPrice) &&
    Number(prev.originalPrice) === Number(next.originalPrice)
  );
}

function areCartTableRowPropsEqual(prev, next) {
  if (prev.rowVersion !== next.rowVersion) return false;
  if (prev.index !== next.index) return false;
  if (String(prev.item?.id ?? "") !== String(next.item?.id ?? "")) return false;
  if (String(prev.item?.product_id ?? prev.item?.productId ?? "") !== String(next.item?.product_id ?? next.item?.productId ?? "")) return false;
  if (Number(prev.item?.qty ?? prev.item?.order_qty ?? 0) !== Number(next.item?.qty ?? next.item?.order_qty ?? 0)) return false;
  if (Number(prev.item?.price ?? prev.item?.unit_price ?? 0) !== Number(next.item?.price ?? next.item?.unit_price ?? 0)) return false;
  if (Number(prev.item?.discount ?? 0) !== Number(next.item?.discount ?? 0)) return false;
  if (String(prev.item?.promotion_id ?? prev.item?.promoId ?? "") !== String(next.item?.promotion_id ?? next.item?.promoId ?? "")) return false;
  if (prev.isKeyboardHighlighted !== next.isKeyboardHighlighted) return false;
  if (prev.isLoading !== next.isLoading) return false;
  if (prev.autoOpenQuantityPopup !== next.autoOpenQuantityPopup) return false;
  if (prev.autoFocusQuantityInput !== next.autoFocusQuantityInput) return false;
  if (prev.quantityInlineEditKey !== next.quantityInlineEditKey) return false;
  if (prev.customerId !== next.customerId) return false;
  if (prev.isEditMode !== next.isEditMode) return false;
  if (prev.isQuickOrder !== next.isQuickOrder) return false;
  if (prev.isRhp !== next.isRhp) return false;
  if (prev.isTelecaller !== next.isTelecaller) return false;
  if (prev.hideTypeColumn !== next.hideTypeColumn) return false;
  if (prev.hideBoxPriceColumn !== next.hideBoxPriceColumn) return false;
  if (prev.hideVatColumn !== next.hideVatColumn) return false;
  if (prev.rhpReadOnlyPricing !== next.rhpReadOnlyPricing) return false;
  if (prev.enableProductSplit !== next.enableProductSplit) return false;
  if (prev.showVatAsAmount !== next.showVatAsAmount) return false;
  if (prev.showVatBoth !== next.showVatBoth) return false;
  if (prev.enableVatTooltip !== next.enableVatTooltip) return false;
  if (prev.onTypeChange !== next.onTypeChange) return false;
  if (prev.onAddAsNewType !== next.onAddAsNewType) return false;
  if (prev.onQuantityChange !== next.onQuantityChange) return false;
  if (prev.onPriceChange !== next.onPriceChange) return false;
  if (prev.onDiscountChange !== next.onDiscountChange) return false;
  if (prev.onDiscountCommentChange !== next.onDiscountCommentChange) return false;
  if (prev.onDelete !== next.onDelete) return false;
  if (prev.onValidationError !== next.onValidationError) return false;
  if (prev.onAllotQtyChange !== next.onAllotQtyChange) return false;
  if (prev.onQuantityPopupClose !== next.onQuantityPopupClose) return false;
  if (prev.onQuantityPopupOpen !== next.onQuantityPopupOpen) return false;
  if (prev.onDiscountChangeComplete !== next.onDiscountChangeComplete) return false;
  if (prev.onPriceChangeComplete !== next.onPriceChangeComplete) return false;
  if (prev.onPromoQtyLineOpenDrawer !== next.onPromoQtyLineOpenDrawer) return false;
  if (prev.onPromoQtyOpenPromotionsModal !== next.onPromoQtyOpenPromotionsModal) return false;
  if (prev.onQtyRegionFocusChange !== next.onQtyRegionFocusChange) return false;
  if (!areItemDetailsEqual(prev.itemDetails, next.itemDetails)) return false;
  return true;
}

export default memo(CartTableRow, areCartTableRowPropsEqual);
